import React, { useState, useEffect } from 'react';
import { fetchWithRetry } from './utils/api';
import MMCustomPaginator from './MMCustomPaginator';
import { format } from 'date-fns';
import './insurance-products.css';

const dateFormat = (date) => {
  return <span>{format(date, 'dd MMM yyyy')}</span>;
};

const MMProductList = ({ config, hasInvestmentCategory }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [latestNavDate, setLatestNavDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('top');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showFeatures, setShowFeatures] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [navData, setNavData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('sort_order');
  const [typeOfProduct, setTypeOfProduct] = useState(null);

  useEffect(() => {
    fetchProductData();
  }, [first, rows, sortField, sortOrder, activeTab, searchTerm, sortOption]);

  const fetchProductData = async () => {
    try {
      setLoading(true);
      const offset = first;
      const limit = rows;
      const sort_by = sortOption;
      const sort_order = sortOrder === 1 ? 'asc' : 'desc';
      const criteria = activeTab === 'mm' ? 'mm' : '';

      // Build query params
      const queryParams = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
        sort_by,
        sort_order,
        criteria,
        search: searchTerm,
      }).toString();

      const response = await fetchWithRetry(
        `${config.PY_API_URL}v2/category/${encodeURIComponent(config.categoryCode)}/products?${queryParams}`
      );

      if (response && response.data && response.data.products) {
        const products = response.data.products.map(product => ({
          ...product,
          attributes: product.attributes ? JSON.parse(product.attributes) : []
        }));
        setTypeOfProduct(products?.[0]?.product_type_code);
        
        const validDates = products
          .map(product => new Date(product.last_nav_date))
          .filter(date => !isNaN(date) && date != null && date != '');
        
        const latestDate = validDates.length > 0 ? 
          new Date(Math.max(...validDates)) : new Date();
        
        setLatestNavDate(latestDate);
        setProducts(products);
        setNavData(response.data.nav_data);
        setTotalRecords(response.data.total_count);
      } else {
        setProducts([]);
        setTotalRecords(0);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product data:', error);
      setLoading(false);
      setProducts([]);
      setTotalRecords(0);
    }
  };

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const formattedDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(latestNavDate);

  const renderInsuranceCard = (fund) => {
    // The attributes are already parsed in fetchProductData
    const attributes = fund.attributes || [];
    
    // Helper function to parse nested JSON values
    const getJsonValue = (key) => {
        const attr = attributes.find(attr => attr.key === key);
        if (!attr) return null;
        try {
            return JSON.parse(attr.value);
        } catch (e) {
            console.error(`Error parsing ${key}:`, e);
            return null;
        }
    };

    const bannerInfo = getJsonValue('Banner Info');
    const coverageDetails = getJsonValue('Coverage Details');
    const features = getJsonValue('Features');
    const allFeatures = getJsonValue('All Features');
    const videoUrl = attributes.find(attr => attr.key === 'Video URL')?.value;

    const handleFeatureClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedProduct(fund);
      setShowFeatures(true);
    };

    const handleVideoClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedProduct(fund);
      setShowVideo(true);
    };

    return (
        <div className="insurance-product-details" style={{width:'100%'}}>
            {
              (bannerInfo && Object.keys(bannerInfo).length > 0) && (
                <div className="insurance-banner">
                {bannerInfo && Object.entries(bannerInfo).map(([key, value]) => (
                    <div key={key} className={`banner-${key}`}>
                        <i className={`fas ${value.icon}`}></i>
                        <span>{value.text}</span>
                    </div>
                ))}
                </div>
              )
            }

            <div className="insurance-header">
                <div className="coverage-details">
                    {
                      (coverageDetails?.amount) && (
                        <div>
                            <div className="coverage-label">Cover amount</div>
                            <div className="coverage-amount">
                            {coverageDetails?.amount || 'N/A'}
                        </div>
                        </div>
                      )
                    }
                    {
                      (coverageDetails?.premium) && (
                        <div>
                            <div className="starting-label">Starting at</div>
                            <div className="premium-amount">
                            {coverageDetails?.premium || 'N/A'}
                        </div>
                        </div>
                      )
                    }
                </div>
                {
                  (coverageDetails?.discount) && (
                    <div className="online-discount">
                      Inclusive of {coverageDetails?.discount}
                  </div>
                )}
            </div>

            <div className="features-list">
                {
                  (features?.length > 0) && (
                    features?.map((feature, index) => (
                      <div key={index} className={`feature-item ${feature.isPrimary ? 'heart' : ''}`}>
                          <i className={`fas ${feature.icon}`}></i>
                          <span>{feature.text}</span>
                      </div>
                    ))
                  )
                }
            </div>
            {
              (features?.length > 0) && (
                <i className="fas fa-info-circle view-more-icon" 
                  onClick={() => setShowFeatures(true)} />
              )
            }

            {showFeatures && (
                <div className="features-popup" onClick={() => setShowFeatures(false)}>
                    <div className="features-popup-content" onClick={e => e.stopPropagation()}>
                        <h3>All Features</h3>
                        {allFeatures?.map((feature, index) => (
                            <div key={index}>
                                <h4>{feature.title}</h4>
                                <p>{feature.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {
              (videoUrl && showVideo) && (
                <div className="features-popup" onClick={() => setShowVideo(false)}>
                    <div className="features-popup-content" onClick={e => e.stopPropagation()}>
                        <iframe 
                            width="100%" 
                            height="315" 
                            src={videoUrl} 
                            frameBorder="0" 
                            allowFullScreen
                        />
                    </div>
                </div>
            )}

            <div className="action-links">
                {
                  (features?.length > 0) && (
                    <a href="#" onClick={handleFeatureClick} className="action-link">
                        View all features <i className="fas fa-chevron-right"></i>
                    </a>
                  )
                }
                {
                  (videoUrl && videoUrl != '') && (
                    <a href="#" onClick={handleVideoClick} className="action-link">
                        Watch plan Video <i className="fas fa-play"></i>
                    </a>
                  )
                }
            </div>
        </div>
    );
  };

  const renderFundReturns = (fund) => {
    const percentageChange = navData.find(nav => parseInt(nav.scheme_code) === parseInt(fund.scheme_code))?.percentage_change;
    const changeColor = percentageChange > 0 ? 'green' : 'red';

    return (
      <div className="col-md-12">
        <div className="fund-returns row" style={{color: '#666666'}}>
          <div className='col-md-4'>
            <h4>1Y Return:</h4>
            <p>{fund.returns_1_years ? `${fund.returns_1_years}%` : 'N/A'}</p>
          </div>
          <div className='col-md-4'>
            <h4>3Y Return:</h4>
            <p>{fund.returns_3_years ? `${fund.returns_3_years}%` : 'N/A'}</p>
          </div>
          <div className='col-md-4'>
            <h4>5Y Return:</h4>
            <p>{fund.returns_5_years ? `${fund.returns_5_years}%` : 'N/A'}</p>
          </div>
          <div className='col-md-4'>
            <h4>1 Day Change:</h4>
            <p style={{ color: changeColor }}>
              {percentageChange ? `${(percentageChange).toFixed(2)}%` : 'N/A'}
            </p>
          </div>
          <div className='col-md-4'>
            <h4>Launch Date:</h4>
            <p>{fund.start_date ? dateFormat(fund.start_date) : 'N/A'}</p>
          </div>
          <div className='col-md-4'>
            <h4>Current NAV:</h4>
            <p>{fund.nav ? fund.nav.toFixed(2) : 'N/A'}</p>
          </div>
          <div className='col-md-4'>
            <h4>AUM:</h4>
            <p>{fund.aum ? fund.aum.toFixed(2) : 'N/A'}</p>
          </div>
          <div className='col-md-4'>
            <h4>TER:</h4>
            <p>{fund.expense_ratio ? fund.expense_ratio + '%' : 'N/A'}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='mm-product-list'>
      <div className='tabs-container'>
        <span 
          className={activeTab === 'top' ? 'active-tab' : ''} 
          onClick={() => setActiveTab('top')}
        >
          Top Products
        </span>
        <span 
          className={activeTab === 'mm' ? 'active-tab' : ''} 
          onClick={() => setActiveTab('mm')}
        >
          MM Recommended
        </span>
      </div>

      <div className='date-and-controls'>
        {hasInvestmentCategory && 
          <div className='as-on-date'>
            As on {formattedDate}
          </div>
        }
        <div className='search-sort-container'>
          <div className='search-input'>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <i className="fas fa-search"></i>
          </div>
          <select value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
            <option value="sort_order">Sort Order</option>
            <option value="sort_order">Default</option>
            <option value="p.name">Name</option>
            {typeOfProduct == 1 && (
              <>
                <option value="d.returns_1yr">1Y Return</option>
                <option value="d.returns_3yr">3Y Return</option>
                <option value="d.returns_5yr">5Y Return</option>
                <option value="d.expense_ratio">Expense Ratio</option>
              </>
            )}
            <option value="p.rating">Rating</option>
            <option value="p.open_date">Launch Date</option>
          </select>
          <div className='sort-order'>
            {sortOrder === 1 ? (
              <i className="fas fa-sort-down" style={{cursor: 'pointer', fontSize: 25, marginTop: 0, color: '#7246e3'}} onClick={() => setSortOrder(0)}></i>
            ) : (
              <i className="fas fa-sort-up" style={{cursor: 'pointer', fontSize: 25, marginTop: 10, color: '#7246e3'}} onClick={() => setSortOrder(1)}></i>
            )}
          </div>
        </div>
      </div>

      <div>
        {loading ? (
          <div className='product-list-loading-container'>
            <div className='product-list-loading-spinner'></div>
          </div>
        ) : (
          <div>
            {products && products.length > 0 ? (
              <div>
                {products.map((fund, index) => (
                  <div key={fund.id} className="fund-card row">
                    <a href={`${config.siteUrl}/amc/${fund.owner_code}`}>
                      <div className="fund-logo col-md-1" style={{background: `${fund.icon_bg_color || '#ccc'}`}}>                        
                        <img src={fund.icon} alt={fund.name} />
                      </div>
                    </a>
                    <a className='product-card-link' href={`${config.siteUrl}/product-detail/${fund.product_code}`}>
                      <div className='col-md-11'>
                        <div className='row'>
                          <div className="fund-info col-md-12">
                            <h2>{fund.name}</h2>
                            <p>{fund.type}</p>
                          </div>
                          
                          {/* For Mutual Funds (type 1) */}
                          {fund.product_type_code == 1 && (
                            <>
                              {renderFundReturns(fund)}
                            </>
                          )}

                          {/* For Insurance Products (type 2) */}
                          {fund.product_type_code == 2 && (
                            renderInsuranceCard(fund)
                          )}
                        </div>
                      </div>
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className='card'>
                  <img style={{width:'200px', margin: 'auto', marginTop: '50px', marginBottom: '50px'}} src='https://www.moneymarche.com/wp-content/uploads/2025/02/product_not_found.jpeg'/>
                  {/* <p>
                    No products found
                  </p> */}
                  </div>
                </>
              )}
          </div>
        )}
      </div>

      {totalRecords > rows && (
        <MMCustomPaginator style={{marginBottom: '20px'}}
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
        />
      )}

      {showFeatures && selectedProduct && (
        <div className="features-popup" onClick={() => setShowFeatures(false)}>
          <div className="features-popup-content" onClick={e => e.stopPropagation()}>
            <button className="popup-close" style={{padding: '10px', lineHeight: '10px'}} onClick={() => setShowFeatures(false)}>
              <i className="fas fa-times"></i>
            </button>
            <h3>All Features</h3>
            {JSON.parse(selectedProduct.attributes?.find(attr => attr.key === 'All Features')?.value || '[]')?.map((feature, index) => (
              <div key={index}>
                <h4>{feature.title}</h4>
                <p>{feature.value}</p>
              </div>
            ))}
            <div className="popup-footer">
              <button 
                onClick={() => setShowFeatures(false)}
                style={{
                  padding: '10px 24px',
                  background: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showVideo && selectedProduct && (
        <div className="features-popup" onClick={() => setShowVideo(false)}>
          <div className="features-popup-content" onClick={e => e.stopPropagation()}>
            <button className="popup-close" style={{padding: '10px', lineHeight: '10px'}} onClick={() => setShowVideo(false)}>
              <i className="fas fa-times"></i>
            </button>
            <iframe 
              width="100%" 
              height="315" 
              src={selectedProduct.attributes?.find(attr => attr.key === 'Video URL')?.value} 
              frameBorder="0" 
              allowFullScreen
            />
            <div className="popup-footer">
              <button 
                onClick={() => setShowVideo(false)}
                style={{
                  padding: '10px 24px',
                  background: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MMProductList;
